<template>
  <div class="rental-info">
    <button class="back-btn" @click="$router.go(-1)">
      <van-icon name="arrow-left" />
    </button>

    <section class="detail-panel" v-if="info.id">
      <div class="detail-summary">
        <h1 class="detail-title">{{ isEn ? toPinyin(info.communityName || '') : (info.communityName || '') }}</h1>
        <p class="address">{{ formatBuildingAddress(info.subareaName, info.buildingName, isEn) }}</p>
        <span class="rent-label">{{ $t('rentalDetail.monthlyRent') }}</span>
        <div class="detail-price">
          <span class="yen">¥</span>
          <span class="num">{{ info.minRent }}{{ info.maxRent && info.maxRent !== info.minRent ? ' - ¥' + info.maxRent : '' }}</span>
          <span class="unit">{{ $t('rentalDetail.perMonth') }}</span>
        </div>
        <div class="building-art"></div>
        <div class="feature-row" v-if="featureTags.length">
          <span class="feature" v-for="tag in featureTags" :key="tag">{{ tag }}</span>
        </div>
      </div>

      <section class="landlord">
        <div class="landlord-info">
          <div class="avatar">
            <van-icon name="user-o" />
          </div>
          <div>
            <div class="landlord-name">{{ formatLandlordName() }}</div>
          </div>
        </div>
        <button class="consult" @click="callLandlord">
          <van-icon name="phone-o" />
          <span>
            <span class="consult-main">{{ $t('rental.callPhone') }}</span>
          </span>
        </button>
      </section>

      <section class="detail-section">
        <h2 class="section-heading">
          <van-icon name="records" />
          {{ $t('rentalDetail.description') }}
        </h2>
        <p class="desc-box">{{ isEn && info.descriptionEn ? info.descriptionEn : info.descriptionZh }}</p>
      </section>

      <section class="detail-section" v-if="facilityTags.length">
        <h2 class="section-heading">
          <van-icon name="home-o" />
          {{ $t('rentalDetail.facilities') }}
        </h2>
        <div class="feature-row">
          <span class="feature facility" v-for="f in facilityTags" :key="f">{{ f }}</span>
        </div>
      </section>

      <section class="detail-section" v-if="galleryImages.length">
        <h2 class="section-heading">
          <van-icon name="photo-o" />
          {{ $t('rentalDetail.photos') }}
        </h2>
        <div class="photo-carousel">
          <button class="photo-nav photo-prev" @click="scrollPhotos(-1)">
            <van-icon name="arrow-left" />
          </button>
          <div class="photos" ref="photosRef">
            <div class="photo" v-for="(img, idx) in galleryImages" :key="idx">
              <img :src="img" alt="">
            </div>
          </div>
          <button class="photo-nav photo-next" @click="scrollPhotos(1)">
            <van-icon name="arrow" />
          </button>
        </div>
      </section>
    </section>
  </div>
</template>

<script>
import Vue from 'vue'
import { pinyin } from 'pinyin-pro'
import { getImageStream } from '@/utils/index'
import { toPinyin, formatBuildingAddress } from './addressUtil'

const compoundSurnames = [
  '欧阳', '司马', '上官', '诸葛', '东方', '独孤', '南宫', '夏侯',
  '尉迟', '皇甫', '慕容', '宇文', '令狐', '长孙', '公孙', '呼延',
  '司徒', '拓跋', '端木', '申屠', '万俟', '闻人', '赫连', '澹台',
  '公羊', '宗政', '濮阳', '太史', '闾丘', '轩辕', '巫马', '乐正',
  '公冶', '漆雕',
]

function getSurname(name) {
  for (const cs of compoundSurnames) {
    if (name.startsWith(cs)) {
      const py = pinyin(cs, { toneType: 'none', type: 'array' })
      return { pinyin: py.join(''), chinese: cs }
    }
  }
  const first = name.charAt(0)
  const py = pinyin(first, { toneType: 'none', type: 'array' })
  return { pinyin: py[0], chinese: first }
}

export default {
  data () {
    return {
      info: {},
      galleryImages: [],
      featureTags: [],
      facilityTags: [],
      tagsDict: [],
      facilityDict: [],
    }
  },
  computed: {
    isEn () {
      return Vue.config.lang === 'en-US'
    },
  },
  created () {
    const id = this.$route.query.id
    if (id) {
      this.loadDetail(id)
    }
    this.loadTags()
  },
  methods: {
    toPinyin,
    formatBuildingAddress,
    loadTags () {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/sys/dict/listDictByCode'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ code: 'landlord_tag' })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.tagsDict = data.dicts || []
        }
      })
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/sys/dict/listDictByCode'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ code: 'landlord_facility' })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.facilityDict = data.dicts || []
        }
      })
    },
    resolveTags (tagIds) {
      if (!tagIds) return []
      let ids = tagIds
      if (typeof ids === 'string') {
        try {
          ids = JSON.parse(ids)
        } catch (e) {
          ids = ids.split(',').map(id => id.trim())
        }
      }
      return ids
        .map(id => String(id))
        .map(id => this.tagsDict.find(d => String(d.value) === id))
        .filter(Boolean)
        .map(d => {
          if (this.isEn && d.extended) {
            try {
              const ext = JSON.parse(d.extended)
              return ext.en || d.label
            } catch (e) { /* fall through */ }
          }
          return d.label
        })
    },
    resolveFacilities (facilityIds) {
      if (!facilityIds) return []
      return facilityIds
        .map(id => String(id))
        .map(id => this.facilityDict.find(d => String(d.value) === id))
        .filter(Boolean)
        .map(d => {
          if (this.isEn && d.extended) {
            try {
              const ext = JSON.parse(d.extended)
              return ext.en || d.label
            } catch (e) { /* fall through */ }
          }
          return d.label
        })
    },
    loadDetail (id) {
      this.$toast.loading({ duration: 0, message: this.$t('common.loading'), forbidClick: true })
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/landlord/listing/info/' + id),
        method: 'get',
        params: this.$httpCustApp.adornParams({ bilingual: true })
      }).then(({ data }) => {
        this.$toast.clear()
        if (data.code === 0 && data.listing) {
          const listing = data.listing
          this.info = listing
          if (listing.images && listing.images.length) {
            this.galleryImages = (listing.images || []).map(img => getImageStream(img))
          }
          this.featureTags = this.resolveTags(listing.tagIds)
          this.facilityTags = this.resolveFacilities(listing.facilityIds)
        } else {
          this.$toast.fail({ duration: 1500, forbidClick: true, message: data.msg || this.$t('common.loadFail') })
        }
      }).catch(() => {
        this.$toast.clear()
      })
    },
    formatLandlordName () {
      const name = (this.info.landlordName || '').trim()
      if (!name) return ''
      const gender = this.info.landlordGender
      const surname = getSurname(name)
      const display = this.isEn
        ? surname.pinyin.charAt(0).toUpperCase() + surname.pinyin.slice(1)
        : surname.chinese
      if (gender === 1) return this.$t('rental.mr', { surname: display })
      if (gender === 2) return this.$t('rental.ms', { surname: display })
      return name
    },
    callLandlord () {
      if (this.info.landlordMobile) {
        window.location.href = 'tel:' + this.info.landlordMobile
      }
    },
    scrollPhotos (direction) {
      const photos = this.$refs.photosRef
      if (photos) {
        photos.scrollBy({ left: direction * 120, behavior: 'smooth' })
      }
    },
  },
}
</script>

<style scoped lang="scss">
.rental-info {
  min-height: 100vh;
  overflow: hidden;
  background: #d6e8f8;

  .back-btn {
    position: fixed;
    z-index: 12;
    top: 31px;
    left: 31px;
    width: 50px;
    height: 50px;
    display: grid;
    place-items: center;
    border: none;
    border-radius: 50%;
    color: #607086;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
    font-size: 31px;
  }

  .detail-panel {
    position: absolute;
    left: 13px;
    right: 13px;
    top: 120px;
    border-radius: 34px 34px 0 0;
    background: #ffffff;
    overflow: hidden;
    padding-bottom: 58px;
    min-height: calc(100vh - 29px);
  }

  .detail-summary {
    position: relative;
    padding: 38px 34px 0;

    .detail-title {
      margin: 0;
      color: #1a1a2e;
      font-size: 38px;
      line-height: 50px;
      font-weight: 800;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .address {
      margin: 8px 0 0;
      color: #888888;
      font-size: 23px;
      line-height: 34px;
    }

    .rent-label {
      display: inline-block;
      margin-top: 23px;
      padding: 4px 15px;
      border-radius: 8px;
      color: #2f7de1;
      background: #ebf3ff;
      font-size: 21px;
      line-height: 32px;
      font-weight: 800;
    }

    .detail-price {
      margin-top: 8px;
      display: flex;
      align-items: baseline;
      color: #f5a623;
      font-weight: 800;

      .yen {
        font-size: 25px;
        margin-right: 4px;
      }

      .num {
        font-size: 57px;
        line-height: 72px;
      }

      .unit {
        margin-left: 8px;
        color: #888888;
        font-size: 25px;
        font-weight: 400;
      }
    }

    // 原型页面 building-art：summary 右上角纯 CSS 建筑装饰
    .building-art {
      position: absolute;
      top: 38px;
      right: 0;
      width: 213px;
      height: 200px;
      overflow: hidden;
      border-radius: 8px;

      &::before {
        content: "";
        position: absolute;
        left: -116px;
        top: -20px;
        width: 403px;
        height: 202px;
        background:
          radial-gradient(circle at 78% 23%, rgba(47, 125, 225, 0.24) 0 60px, transparent 61px),
          linear-gradient(130deg, transparent 0 58%, #d5e5ff 58% 100%);
        border-radius: 0 0 0 115px;
      }
    }
  }

  .feature-row {
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 11px;

    .feature {
      height: 46px;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 0 17px;
      border: 2px solid #c9ddf5;
      border-radius: 23px;
      color: #0d59d8;
      background: #edf5ff;
      font-size: 21px;
      line-height: 32px;
      font-weight: 800;
      white-space: nowrap;

      &.facility {
        border-color: #c8e0d0;
        color: #3f8d5e;
        background: #ecf7f0;
      }
    }
  }

  .detail-summary {
    .feature-row {
      margin-top: 22px;
      gap: 13px;

      .feature {
        height: 54px;
        padding: 0 21px;
        border-radius: 27px;
        font-size: 25px;
        line-height: 36px;
        letter-spacing: 1px;
      }
    }
  }

  .landlord {
    position: relative;
    height: 145px;
    margin: 38px 34px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 34px;
    color: #ffffff;
    background: linear-gradient(90deg, #1a5dbf 0%, #2f7de1 100%);
    border-radius: 15px;

    .landlord-info {
      display: flex;
      align-items: center;
      gap: 19px;
    }

    .avatar {
      width: 84px;
      height: 84px;
      display: grid;
      place-items: center;
      border: 4px solid rgba(255, 255, 255, 0.6);
      border-radius: 50%;
      background: #ffffff;
      color: #71a4ec;
      font-size: 53px;
    }

    .landlord-name {
      font-size: 29px;
      line-height: 43px;
      font-weight: 800;
    }

    .verified {
      margin-top: 4px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 34px;
      padding: 2px 13px;
      border-radius: 38px;
      color: rgba(255, 255, 255, 0.85);
      background: rgba(255, 255, 255, 0.18);
      font-size: 21px;
      line-height: 32px;

      .van-icon {
        font-size: 19px;
      }
    }

    .consult {
      width: 225px;
      height: 84px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 11px;
      border: none;
      border-radius: 42px;
      color: #ffffff;
      background: linear-gradient(160deg, #f5a623 0%, #f5c840 100%);
      box-shadow: 0 4px 8px rgba(245, 166, 35, 0.45);
      font-size: 19px;

      .van-icon {
        font-size: 31px;
      }

      .consult-main {
        display: block;
        font-size: 25px;
        line-height: 27px;
        font-weight: 800;
      }

      .consult-sub {
        display: block;
        margin-top: 2px;
        font-size: 19px;
        line-height: 21px;
        font-weight: 600;
        opacity: 0.88;
      }
    }
  }

  .detail-section {
    padding: 34px 34px 0;

    .section-heading {
      display: flex;
      align-items: center;
      gap: 11px;
      margin: 0;
      color: #1a1a2e;
      font-size: 29px;
      line-height: 43px;
      font-weight: 800;

      .van-icon {
        font-size: 34px;
        color: #2f7de1;
      }
    }

    .desc-box {
      margin-top: 19px;
      padding: 27px 31px 27px 34px;
      border-left: 6px solid #2f7de1;
      border-radius: 23px;
      color: #444444;
      background: #ebf3ff;
      font-size: 25px;
      line-height: 45px;
    }
  }

  .photo-carousel {
    position: relative;
    width: 100%;
    margin-top: 27px;

    .photo-nav {
      position: absolute;
      top: 50%;
      z-index: 2;
      width: 53px;
      height: 53px;
      display: grid;
      place-items: center;
      border: none;
      border-radius: 50%;
      color: #ffffff;
      background: rgba(13, 89, 216, 0.84);
      box-shadow: 0 4px 15px rgba(11, 31, 63, 0.22);
      font-size: 31px;
      transform: translateY(-50%);

      &.photo-prev {
        left: -15px;
      }

      &.photo-next {
        right: -15px;
      }
    }

    .photos {
      display: flex;
      gap: 15px;
      width: 100%;
      overflow-x: auto;
      scroll-behavior: smooth;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;

      &::-webkit-scrollbar {
        display: none;
      }

      .photo {
        flex: 0 0 auto;
        width: 216px;
        height: 223px;
        border-radius: 19px;
        overflow: hidden;
        scroll-snap-align: start;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
    }
  }
}
</style>
